import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../../../lib/api';

// Clienti collegati all'insegna, in sola consultazione: l'associazione si fa
// dalla scheda del nominativo (tab Cliente → Insegna).

interface ClienteInsegna {
  id: number;
  codice: string;
  denominazione: string;
  partitaIva: string | null;
  citta: string | null;
  provincia: string | null;
  categoriaCliente: string;
  puntiVendita: number;
  disattivato: boolean;
}

const CATEGORIA_LABEL: Record<string, string> = {
  CATENA_GDO: 'Catena GDO',
  AZIENDA_B2B: 'Azienda B2B',
  PRIVATO_B2C: 'Privato B2C',
  AGENTE: 'Agente',
};

interface Props {
  insegnaId: number;
}

export default function TabClientiInsegna({ insegnaId }: Props) {
  const navigate = useNavigate();
  const [clienti, setClienti] = useState<ClienteInsegna[]>([]);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState('');
  const [filtro, setFiltro] = useState('');

  useEffect(() => {
    let annullato = false;
    (async () => {
      setLoading(true);
      setErrore('');
      try {
        // @api GET /api/anagrafiche/insegne/:id/clienti -> {"success":true,"data":[{"id":12,"codice":"NOM00012","denominazione":"Bricocenter Italia S.r.l.","partitaIva":"01234567890","citta":"Milano","provincia":"MI","categoriaCliente":"CATENA_GDO","puntiVendita":4,"disattivato":false}]}
        const res = await apiFetch<{ success: boolean; data: ClienteInsegna[] }>(`/api/anagrafiche/insegne/${insegnaId}/clienti`);
        if (!annullato) setClienti(res.data || []);
      } catch (err: any) {
        if (!annullato) setErrore(err.message || 'Errore nel caricamento dei clienti collegati');
      } finally {
        if (!annullato) setLoading(false);
      }
    })();
    return () => { annullato = true; };
  }, [insegnaId]);

  const filtrati = useMemo(() => {
    const f = filtro.trim().toLowerCase();
    if (!f) return clienti;
    return clienti.filter((c) =>
      c.denominazione.toLowerCase().includes(f)
      || c.codice.toLowerCase().includes(f)
      || (c.partitaIva || '').toLowerCase().includes(f)
      || (c.citta || '').toLowerCase().includes(f)
    );
  }, [clienti, filtro]);

  return (
    <div className="bop-card">
      <div className="bop-card__header">
        <h3 className="bop-card__title">Clienti collegati</h3>
        {clienti.length > 0 && (
          <input
            className="bop-input bop-insegna-scheda__filtro"
            placeholder="Filtra per nome, codice, P.IVA o città…"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
          />
        )}
      </div>
      <div className="bop-card__body">
        <p className="bop-text-muted bop-insegna-scheda__intro">
          I nominativi che hanno questa insegna nella scheda cliente. Per collegarne
          uno nuovo apri il nominativo e scegli l'insegna nella scheda Cliente.
        </p>

        {loading ? (
          <div className="bop-empty-state"><p className="bop-empty-state__text">Caricamento…</p></div>
        ) : errore ? (
          <p className="bop-text-danger">{errore}</p>
        ) : clienti.length === 0 ? (
          <div className="bop-empty-state">
            <p className="bop-empty-state__text">Nessun cliente collegato a questa insegna</p>
          </div>
        ) : filtrati.length === 0 ? (
          <div className="bop-empty-state">
            <p className="bop-empty-state__text">Nessun cliente corrisponde al filtro</p>
          </div>
        ) : (
          <table className="bop-table bop-table--clickable">
            <thead>
              <tr>
                <th>Codice</th>
                <th>Cliente</th>
                <th>P.IVA / C.F.</th>
                <th>Sede legale</th>
                <th>Categoria</th>
                <th className="bop-text-right">Punti vendita</th>
              </tr>
            </thead>
            <tbody>
              {filtrati.map((c) => (
                <tr key={c.id} onClick={() => navigate(`/anagrafiche/nominativi/${c.id}`)}>
                  <td className="bop-text-muted">{c.codice}</td>
                  <td>
                    <strong>{c.denominazione}</strong>
                    {c.disattivato && (
                      <span className="bop-badge bop-badge--neutral bop-insegna-scheda__badge-riga">Disattivato</span>
                    )}
                  </td>
                  <td>{c.partitaIva || '—'}</td>
                  <td>{c.citta ? `${c.citta}${c.provincia ? ` (${c.provincia})` : ''}` : '—'}</td>
                  <td>{CATEGORIA_LABEL[c.categoriaCliente] || c.categoriaCliente}</td>
                  <td className="bop-text-right">{c.puntiVendita}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
